const IMAGE_EXTENSIONS = /\.(?:avif|gif|ico|jpe?g|png|svg|webp)(?:$|[?#])/i;
const SKIPPED_PREFIXES = [
  "/media/",
  "/resource-cover-templates/",
  "/resources/",
];

export function minioAssetUrl(value) {
  if (!value || /^(?:data:|blob:|mailto:|tel:|#)/i.test(value)) return value;

  let url;
  try {
    url = new URL(value, window.location.origin);
  } catch {
    return value;
  }

  if (
    url.origin !== window.location.origin ||
    !IMAGE_EXTENSIONS.test(url.pathname)
  )
    return value;
  if (SKIPPED_PREFIXES.some((prefix) => url.pathname.startsWith(prefix)))
    return value;

  const cleanPath = url.pathname.replace(/^\/+/, "");
  return `${window.location.origin}/media/static/${cleanPath}${url.search}`;
}

function rewriteSrcset(value) {
  return value
    .split(",")
    .map((candidate) => {
      const [source, ...descriptor] = candidate.trim().split(/\s+/);
      return [minioAssetUrl(source), ...descriptor].join(" ");
    })
    .join(", ");
}

function rewriteCssValue(value) {
  return value.replace(/url\((['"]?)(.*?)\1\)/gi, (match, quote, source) => {
    const rewritten = minioAssetUrl(source);
    return rewritten === source ? match : `url("${rewritten}")`;
  });
}

function rewriteElement(element) {
  if (!(element instanceof Element)) return;

  ["src", "href", "poster"].forEach((attribute) => {
    if (!element.hasAttribute(attribute)) return;
    const current = element.getAttribute(attribute);
    const rewritten = minioAssetUrl(current);
    if (rewritten !== current) element.setAttribute(attribute, rewritten);
  });

  if (element.hasAttribute("srcset")) {
    element.setAttribute(
      "srcset",
      rewriteSrcset(element.getAttribute("srcset")),
    );
  }

  if (element.hasAttribute("style")) {
    const current = element.getAttribute("style");
    const rewritten = rewriteCssValue(current);
    if (rewritten !== current) element.setAttribute("style", rewritten);
  }
}

export function rewriteAssetMarkup(markup) {
  if (!markup || typeof document === "undefined") return markup;

  const template = document.createElement("template");
  template.innerHTML = markup;
  template.content
    .querySelectorAll(
      "img,source,video,input[type='image'],link[rel~='icon'],[style]",
    )
    .forEach(rewriteElement);

  return template.innerHTML;
}

function rewriteStyleRule(rule) {
  if (rule.cssRules) {
    [...rule.cssRules].forEach(rewriteStyleRule);
    return;
  }

  if (!rule.style) return;
  [...rule.style].forEach((property) => {
    const current = rule.style.getPropertyValue(property);
    const rewritten = rewriteCssValue(current);
    if (rewritten !== current) {
      rule.style.setProperty(
        property,
        rewritten,
        rule.style.getPropertyPriority(property),
      );
    }
  });
}

function rewriteStyleSheets() {
  [...document.styleSheets].forEach((sheet) => {
    try {
      [...(sheet.cssRules ?? [])].forEach(rewriteStyleRule);
    } catch {
      // Cross-origin stylesheets are intentionally left untouched.
    }
  });
}

function rewriteComputedBackgrounds(root = document) {
  const elements =
    root === document
      ? document.querySelectorAll("*")
      : [root, ...root.querySelectorAll("*")];
  elements.forEach((element) => {
    if (!(element instanceof Element)) return;
    const current = window.getComputedStyle(element).backgroundImage;
    const rewritten = rewriteCssValue(current);
    if (rewritten !== current) element.style.backgroundImage = rewritten;
  });
}

function rewriteAddedNode(node) {
  rewriteElement(node);
  node
    .querySelectorAll?.(
      "img,source,video,input[type='image'],link[rel~='icon'],[style]",
    )
    .forEach(rewriteElement);
  if (node instanceof Element) {
    window.requestAnimationFrame(rewriteComputedBackgrounds.bind(null, node));
  }
}

function rewriteMutations(mutations) {
  mutations.forEach((mutation) => {
    if (mutation.type === "attributes") rewriteElement(mutation.target);
    mutation.addedNodes.forEach(rewriteAddedNode);
  });
}

export function installMinioAssetRewriter() {
  document
    .querySelectorAll(
      "img,source,video,input[type='image'],link[rel~='icon'],[style]",
    )
    .forEach(rewriteElement);
  rewriteStyleSheets();
  window.requestAnimationFrame(() => rewriteComputedBackgrounds());

  const observer = new MutationObserver(rewriteMutations);

  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ["src", "srcset", "href", "poster", "style"],
  });

  window.addEventListener(
    "load",
    () => {
      rewriteStyleSheets();
      rewriteComputedBackgrounds();
    },
    { once: true },
  );
  return () => observer.disconnect();
}
